import { extractApiErrorMessage } from '@cartwheel/api'
import {
  type CostEstimate,
  clientCostEstimateShowQueryKey,
  type MedicaidCostEstimateInput,
  useClientCostEstimateCreateMutation,
  useClientCostEstimateUpdateMutation,
} from '@cartwheel/api/v1'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '../button'
import { TextInput } from '../text-input'

interface MedicaidEstimateFormProps {
  clientId: string
  estimate: CostEstimate | null
  onSaved: () => void
  onCancel: () => void
}

export function MedicaidEstimateForm({
  clientId,
  estimate,
  onSaved,
  onCancel,
}: MedicaidEstimateFormProps) {
  const editable = estimate?.editable
  const [sessionLimit, setSessionLimit] = useState<number | null>(editable?.session_limit ?? null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: clientCostEstimateShowQueryKey({ path: { client_id: clientId } }),
    })

  const createMutation = useClientCostEstimateCreateMutation({
    onSuccess: () => {
      invalidate()
      onSaved()
    },
    onError: (error) =>
      setErrorMessage(extractApiErrorMessage(error, 'Something went wrong. Please try again.')),
  })
  const updateMutation = useClientCostEstimateUpdateMutation({
    onSuccess: () => {
      invalidate()
      onSaved()
    },
    onError: (error) =>
      setErrorMessage(extractApiErrorMessage(error, 'Something went wrong. Please try again.')),
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setErrorMessage(null)

    const body: MedicaidCostEstimateInput = {
      estimate_type: 'medicaid',
      session_limit: sessionLimit,
    }

    if (estimate?.id) {
      updateMutation.mutate({ path: { client_id: clientId, id: estimate.id }, body })
    } else {
      createMutation.mutate({ path: { client_id: clientId }, body })
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: TextInput renders the <input> */}
      <label className="flex justify-between items-center py-2 px-4 border-b border-t border-gray-stroke">
        <span className="font-medium">Medicaid</span>
        <div className="flex items-center">
          <span className="mr-1">$</span>
          <span className="w-[125px]">
            <TextInput value="0" disabled className="text-right bg-white-dark h-8" />
          </span>
        </div>
      </label>

      <div>
        {/* biome-ignore lint/a11y/noLabelWithoutControl: TextInput renders the <input> */}
        <label className="flex justify-between items-center py-2 px-4 border-b border-gray-stroke">
          <span className="font-medium">Session Limits (optional)</span>
          <span className="w-[125px] block">
            <TextInput
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={sessionLimit ?? ''}
              onChange={(event) => {
                const next = event.target.value
                setSessionLimit(next === '' ? null : Number(next))
              }}
              className="text-right h-8"
            />
          </span>
        </label>

        {sessionLimit !== null && (
          <div className="text-brick-light p-4 mt-4">
            <div className="flex items-start">
              <i className="fa-solid fa-warning mr-2 mt-1" aria-hidden />
              <div>
                Session limits on Medicaid require Care Coordination review, this will automatically
                move to "Cost confirmation needed" upon saving.
              </div>
            </div>
          </div>
        )}
      </div>

      {errorMessage && <div className="mt-2 text-red-700 text-sm text-right">{errorMessage}</div>}

      <div className="flex justify-end gap-2 mt-10">
        <Button type="button" variant="tertiary" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}
